import classNames from 'classnames'
import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './InfoTooltip.module.css'

export type InfoTooltipPlacement = 'top' | 'bottom' | 'left' | 'right'

interface InfoTooltipProps {
    content: React.ReactNode
    children?: React.ReactNode
    placement?: InfoTooltipPlacement
}

export const InfoTooltip = ({ content, children, placement = 'top' }: InfoTooltipProps) => {
    const triggerRef = useRef<HTMLSpanElement | null>(null)
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)

    const updateCoords = () => {
        const el = triggerRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        switch (placement) {
            case 'top':
                setCoords({ top: rect.top, left: rect.left + rect.width / 2 })
                break
            case 'bottom':
                setCoords({ top: rect.bottom, left: rect.left + rect.width / 2 })
                break
            case 'left':
                setCoords({ top: rect.top + rect.height / 2, left: rect.left })
                break
            case 'right':
                setCoords({ top: rect.top + rect.height / 2, left: rect.right })
                break
        }
    }

    const trigger = children ?? <i className={classNames(styles.icon, 'material-icons')}>info</i>
    if (!content) return <>{trigger}</>

    return (
        <>
            <span
                ref={triggerRef}
                className={styles.wrapper}
                onMouseEnter={updateCoords}
                onMouseLeave={() => setCoords(null)}
                onFocus={updateCoords}
                onBlur={() => setCoords(null)}
                tabIndex={0}
            >
                {trigger}
            </span>
            {coords &&
                createPortal(
                    <span
                        className={classNames(styles.tooltip, styles[placement])}
                        style={{ top: coords.top, left: coords.left }}
                        role="tooltip"
                    >
                        {content}
                    </span>,
                    document.body
                )}
        </>
    )
}
